import { useEffect, useMemo, useState } from "react";
import type { Control } from "react-hook-form";
import { useController } from "react-hook-form";

import Autocomplete from "@/components/Autocomplete";
import Checkbox from "@/components/Checkbox";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import Progress from "@/components/Progress";
import ProgressCard from "@/components/ProgressCard";
import SubSectionHeading from "@/components/SubSectionHeading";

import { type ExperienceIndexHubState } from "./careerPlanHubSummaries";
import {
  careerPlanOnboardingExperienceIndexGapLabel,
  toggleCareerPlanOnboardingSelectedId,
  type CareerPlanOnboardingExperienceIndexGroup,
  type CareerPlanOnboardingFormValues
} from "./careerPlanOnboarding";
import {
  EXPERIENCE_INDEX_SCORE_MAX,
  experienceIndexScorePercent,
  formatExperienceIndexScoreValue
} from "./experienceIndexResults";

export type CareerPlanOnboardingComparisonRoleOption = {
  id: string;
  title: string;
};

export default function CareerPlanOnboardingExperienceIndexStep({
  control,
  groups,
  roleOptions,
  hubState,
  loading,
  errorMessage
}: {
  control: Control<CareerPlanOnboardingFormValues>;
  groups: CareerPlanOnboardingExperienceIndexGroup[];
  roleOptions: CareerPlanOnboardingComparisonRoleOption[];
  hubState: ExperienceIndexHubState;
  loading: boolean;
  errorMessage?: string;
}) {
  const { field } = useController({ control, name: "selectedExperienceIndexIds" });
  const { field: comparisonRole } = useController({ control, name: "comparisonRoleId" });
  const selectedRole = roleOptions.find((role) => role.id === comparisonRole.value) ?? null;
  const [query, setQuery] = useState(selectedRole?.title ?? "");
  const filteredRoles = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return roleOptions;
    return roleOptions.filter((role) => role.title.toLowerCase().includes(normalizedQuery));
  }, [query, roleOptions]);

  useEffect(() => {
    setQuery(selectedRole?.title ?? "");
  }, [selectedRole?.title]);

  if (loading) {
    return (
      <div className="flex justify-center py-8" data-testid="career-plan-onboarding-experience-index-loading">
        <Progress />
      </div>
    );
  }

  if (errorMessage) {
    return <ErrorBoundary variant="inline" title="Unable to Load Experience Index" message={errorMessage} />;
  }

  const selectedIds = field.value;
  const emptyCaption =
    hubState === "finished"
      ? "Experience Index results are not available yet."
      : "This person has not submitted their Experience Index assessment yet.";
  const emptyTitle = hubState === "finished" ? "No Experience Index Results" : "No Experience Index";

  return (
    <div className="flex flex-col gap-6" data-testid="career-plan-onboarding-experience-index">
      <p className="text-headline-small">
        These are the strengths and gaps from this person's Experience Index. Uncheck anything that is stale or
        off-limits for this plan.
      </p>
      {groups.length > 0 && roleOptions.length > 0 ? (
        <div
          onBlur={() => {
            setQuery(selectedRole?.title ?? "");
          }}
        >
          <Autocomplete
            data-testid="career-plan-onboarding-comparison-role"
            variant="searchableDropdown"
            label="Comparison Role"
            query={query}
            data={filteredRoles}
            menuClassName="max-h-[20rem]"
            onChangeText={setQuery}
            onSelect={(role) => {
              comparisonRole.onChange(role.id);
              setQuery(role.title);
            }}
            reset={() => {
              comparisonRole.onChange("");
              setQuery("");
            }}
            onMouseDown={(event: MouseEvent) => {
              event.preventDefault();
            }}
            renderItem={(role) => <div className="text-body-large">{role.title}</div>}
          />
        </div>
      ) : null}
      {groups.length === 0 ? (
        <div data-testid="career-plan-onboarding-experience-index-empty">
          <EmptyState iconName="quiz" title={emptyTitle} caption={emptyCaption} />
        </div>
      ) : (
        groups.map((group) => (
          <div
            className="flex flex-col gap-4"
            data-testid={`career-plan-onboarding-experience-index-${group.id}`}
            key={group.id}
          >
            <div>
              <SubSectionHeading className={group.description ? "pb-1" : undefined}>{group.label}</SubSectionHeading>
              {group.description ? (
                <p className="text-body-medium text-sys-brand-on-surface-variant">{group.description}</p>
              ) : null}
            </div>
            {group.items.map((item) => {
              const checked = selectedIds.includes(item.id);
              const gapLabel =
                group.id === "focusAreas" ? careerPlanOnboardingExperienceIndexGapLabel(item.gap) : undefined;
              return (
                <div className={`flex items-start gap-3 ${checked ? "" : "opacity-60"}`} key={item.id}>
                  <Checkbox
                    name={`career-plan-onboarding-experience-index-${item.id}`}
                    checked={checked}
                    aria-label={item.label}
                    onChange={(nextChecked) => {
                      if (nextChecked === checked) return;
                      field.onChange(toggleCareerPlanOnboardingSelectedId(selectedIds, item.id));
                    }}
                  />
                  <div className="min-w-0 flex-1">
                    <ProgressCard
                      title={item.label}
                      rowLabel="Score"
                      percent={experienceIndexScorePercent(item.score)}
                      valueLabel={formatExperienceIndexScoreValue(item.score)}
                      completed={item.score}
                      total={EXPERIENCE_INDEX_SCORE_MAX}
                      detailText={
                        item.target == null
                          ? `${formatExperienceIndexScoreValue(item.score)} of ${EXPERIENCE_INDEX_SCORE_MAX}`
                          : `Target ${formatExperienceIndexScoreValue(item.target)}`
                      }
                      statusLabel={gapLabel}
                      statusTagType={gapLabel ? "warn" : undefined}
                      progressTone={group.id === "strengths" ? "success" : "warn"}
                      variant="target"
                      targetPercent={item.target == null ? undefined : experienceIndexScorePercent(item.target)}
                      testId={`career-plan-onboarding-experience-index-card-${item.id}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}
